import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FaRegLightbulb, FaRegHandshake, FaCog, FaChartLine, FaHeadset, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useApiData } from '../hooks/useApiData';
import { fetchHowItWorksSection, fetchImage } from '../repository/api';

const HowItWorks = () => {
  const navigate = useNavigate();
  const [expandedSteps, setExpandedSteps] = useState({});
  const [hoveredStep, setHoveredStep] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Fetch how it works data from API
  const { data: howItWorksData, loading, error } = useApiData(fetchHowItWorksSection);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const toggleStepDetails = (stepId) => {
    if (isMobile) {
      setExpandedSteps(prev => ({
        ...prev,
        [stepId]: !prev[stepId]
      }));
    }
  };

  // Handle mouse enter/leave for desktop hover effects
  const handleStepHover = (stepId) => {
    if (!isMobile) {
      setHoveredStep(stepId);
    }
  };

  const handleStepLeave = () => {
    if (!isMobile) {
      setHoveredStep(null);
    }
  };

  const handleContactClick = () => {
    navigate('/contact');
  };

  // Default fallback data
  const defaultSteps = [
    {
      id: 'step-1',
      icon: <FaRegLightbulb />,
      title: "Consultation",
      description: "We start with an in-depth consultation to understand your business needs and objectives.",
      details: "Our experts will analyze your requirements, target audience, and current systems to provide tailored recommendations that align with your business goals.",
      color: "#2090B0"
    },
    {
      id: 'step-2',
      icon: <FaRegHandshake />,
      title: "Solution Design",
      description: "Our team creates a custom solution based on your specific requirements.",
      details: "We develop a comprehensive solution architecture and implementation plan, considering scalability, security, and integration with your existing systems.",
      color: "#105090"
    },
    {
      id: 'step-3',
      icon: <FaCog />,
      title: "Implementation",
      description: "We deploy your solution with minimal disruption to your operations.",
      details: "Our technical team handles all aspects of setup, configuration, testing, and deployment to ensure a smooth transition and successful implementation.",
      color: "#2090B0"
    },
    {
      id: 'step-4',
      icon: <FaChartLine />,
      title: "Optimization",
      description: "We continuously monitor and optimize your solution for peak performance.",
      details: "Through regular performance analysis and adjustments, we ensure your solution delivers maximum efficiency and return on investment.",
      color: "#105090"
    },
    {
      id: 'step-5',
      icon: <FaHeadset />,
      title: "Ongoing Support",
      description: "Our dedicated support team is available to assist with any questions or issues.",
      details: "We provide comprehensive training, documentation, and 24/7 technical support to ensure your team can fully leverage the implemented solutions.",
      color: "#2090B0"
    }
  ];

  // Transform API data to match component structure
  const transformApiData = (apiData) => {
    if (!apiData || !apiData.steps) return defaultSteps;
    
    const iconMap = [
      <FaRegLightbulb />,
      <FaRegHandshake />,
      <FaCog />,
      <FaChartLine />,
      <FaHeadset />
    ];

    const colorMap = ['#2090B0', '#105090', '#2090B0', '#105090', '#2090B0'];

    return apiData.steps.map((step, index) => ({
      id: `step-${index + 1}`,
      icon: iconMap[index] || <FaRegLightbulb />,
      title: step.title,
      description: step.brief_paragraph,
      details: step.detailed_paragraph,
      color: colorMap[index] || '#2090B0'
    }));
  };

  // Use API data or fallback to default
  const steps = howItWorksData ? transformApiData(howItWorksData) : defaultSteps;
  const sectionHeading = howItWorksData?.heading || 'How It Works';
  const sectionDescription = howItWorksData?.explanation || 'Our streamlined process ensures efficient implementation and excellent results';

  // Show loading state while fetching data
  if (loading) {
    return (
      <Section id="how-it-works">
        <Container>
          <SectionHeader>
            <SectionTitle>How It <HighlightText>Works</HighlightText></SectionTitle>
            <SectionDescription>
              Loading process steps...
            </SectionDescription>
          </SectionHeader>
        </Container>
      </Section>
    );
  }

  // Show error state if API call failed
  if (error) {
    console.error('How it works section error:', error);
    // Continue with default data instead of showing error
  }

  return (
    <Section id="how-it-works">
      <BackgroundShapes>
        {['shape-1', 'shape-2', 'shape-3', 'shape-4'].map((id, index) => (
          <BackgroundShape 
            key={id}
            className={['circle', 'triangle', 'square', 'hexagon'][index]}
            top={['10%', undefined, undefined, '60%'][index]}
            right={['5%', undefined, undefined, '15%'][index]}
            bottom={[undefined, '20%', '70%', undefined][index]}
            left={[undefined, '5%', '20%', undefined][index]}
            size={['200px', '150px', '100px', '120px'][index]}
            color={index % 2 === 0 ? '#105090' : '#2090B0'}
          />
        ))}
      </BackgroundShapes>

      <Container>
        <SectionHeader>
          <SectionTitle>
            {sectionHeading.includes('Works') ? (
              <>
                {sectionHeading.replace('Works', '').trim()} <HighlightText>Works</HighlightText>
              </>
            ) : (
              sectionHeading
            )}
          </SectionTitle>
          <SectionDescription>
            {sectionDescription}
          </SectionDescription>
        </SectionHeader>

        <ProcessFlow>
          {steps.map((step, index) => (
            <ProcessStep 
              key={step.id}
              delay={index * 0.1}
              isExpanded={expandedSteps[step.id]}
              onClick={() => isMobile && toggleStepDetails(step.id)}
              onMouseEnter={() => handleStepHover(step.id)}
              onMouseLeave={handleStepLeave}
            >
              <StepConnector 
                delay={index * 0.1} 
                isLast={index === steps.length - 1} 
              />
              
              <StepIconWrapper 
                color={step.color} 
                isActive={!isMobile && hoveredStep === step.id}
              >
                <StepIcon delay={index * 0.1}>
                  {step.icon}
                </StepIcon>
                <StepNumber>{index + 1}</StepNumber>
              </StepIconWrapper>
              
              <StepContent>
                <TitleContainer>
                  <StepTitle color={step.color}>{step.title}</StepTitle>
                  
                  {isMobile && (
                    <ExpandButton 
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleStepDetails(step.id);
                      }}
                      color={step.color}
                      isExpanded={expandedSteps[step.id]}
                    >
                      {expandedSteps[step.id] ? <FaChevronUp /> : <FaChevronDown />}
                    </ExpandButton>
                  )}
                </TitleContainer>
                
                <StepDescription delay={index * 0.15}>
                  {step.description}
                </StepDescription>
                
                <StepDetails 
                  isActive={isMobile ? expandedSteps[step.id] : hoveredStep === step.id} 
                  delay={(index + 1) * 0.1}
                >
                  {step.details}
                </StepDetails>
              </StepContent>
            </ProcessStep>
          ))}
        </ProcessFlow>

        <CtaContainer>
          <CtaText>Ready to get started with Codewave</CtaText>
          <CtaButton onClick={handleContactClick}>Contact Us Today</CtaButton>
        </CtaContainer>
      </Container>
    </Section>
  );
};

export default HowItWorks;

// === Animations ===
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const scaleIn = keyframes`
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
`;

const growLine = keyframes`
  from {
    height: 0;
  }
  to {
    height: 100%;
  }
`;

const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.1);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.8;
  }
`;

const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const floatAnimation = keyframes`
  0% { transform: translate(0, 0) rotate(0deg); }
  25% { transform: translate(5px, -5px) rotate(2deg); }
  50% { transform: translate(0, 0) rotate(0deg); }
  75% { transform: translate(-5px, 5px) rotate(-2deg); }
  100% { transform: translate(0, 0) rotate(0deg); }
`;

// === Styled Components ===
const Section = styled.section`
  padding: 120px 30px;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #fcfcfd 0%, #f5f7fa 100%);
  min-height: 100vh;
  
  @media (max-width: 992px) {
    padding: 80px 30px;
  }
  
  @media (max-width: 576px) {
    padding: 60px 20px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const BackgroundShapes = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
`;

const BackgroundShape = styled.div`
  position: absolute;
  width: ${props => props.size};
  height: ${props => props.size};
  top: ${props => props.top || 'auto'};
  left: ${props => props.left || 'auto'};
  right: ${props => props.right || 'auto'};
  bottom: ${props => props.bottom || 'auto'};
  background: ${props => `${props.color}15`};
  opacity: 0.5;
  animation: ${floatAnimation} ${props => 10 + Math.random() * 10}s infinite ease-in-out;
  
  &.circle {
    border-radius: 50%;
  }
  
  &.triangle {
    clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
  }
  
  &.square {
    transform: rotate(45deg);
  }
  
  &.hexagon {
    clip-path: polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%);
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 80px;
  animation: ${fadeIn} 0.4s ease-out;
  
  @media (max-width: 768px) {
    margin-bottom: 60px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 42px;
  font-weight: 700;
  margin-bottom: 20px;
  color: #105090;
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
  
  @media (max-width: 576px) {
    font-size: 30px;
  }
`;

const HighlightText = styled.span`
  background: linear-gradient(90deg, #105090, #2090B0);
  background-size: 200% auto;
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  animation: ${gradientAnimation} 4s ease infinite;
`;

const SectionDescription = styled.p`
  font-size: 18px;
  line-height: 1.6;
  max-width: 700px;
  margin: 0 auto;
  color: #666;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ProcessFlow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 800px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    gap: 10px;
  }
`;

const ProcessStep = styled.div`
  display: flex;
  position: relative;
  padding: 20px;
  animation: ${fadeIn} 0.4s ease ${props => props.delay}s;
  cursor: ${props => props.isMobile ? 'pointer' : 'default'};
  
  &:hover {
    background: rgba(255, 255, 255, 0.5);
    border-radius: 12px;
    box-shadow: ${props => props.isExpanded ? '0 10px 30px rgba(0, 0, 0, 0.05)' : 'none'};
    transition: all 0.3s ease;
  }
  
  @media (max-width: 576px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 15px 10px;
    background: ${props => props.isExpanded ? 'rgba(255, 255, 255, 0.5)' : 'transparent'};
    border-radius: ${props => props.isExpanded ? '12px' : '0'};
    box-shadow: ${props => props.isExpanded ? '0 10px 20px rgba(0, 0, 0, 0.05)' : 'none'};
  }
`;

const StepConnector = styled.div`
  position: absolute;
  top: 90px;
  left: 35px;
  width: 2px;
  background: linear-gradient(to bottom, #105090, #2090B0);
  height: ${props => props.isLast ? '0' : '100%'};
  z-index: -1;
  animation: ${growLine} 1s ease ${props => props.delay + 0.2}s;
  
  @media (max-width: 576px) {
    left: 30px;
    top: 70px;
    height: ${props => props.isLast ? '0' : '30px'};
  }
`;

const StepIconWrapper = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: ${props => `${props.color}10`};
  border: 2px solid ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 25px;
  position: relative;
  transition: all 0.3s ease;
  transform: ${props => props.isActive ? 'scale(1.1)' : 'scale(1)'};
  box-shadow: ${props => props.isActive ? `0 0 20px ${props.color}50` : 'none'};
  
  @media (max-width: 576px) {
    width: 60px;
    height: 60px;
    margin-right: 0;
    margin-bottom: 15px;
  }
`;

const StepIcon = styled.div`
  font-size: 26px;
  color: inherit;
  animation: ${scaleIn} 0.5s ease ${props => props.delay + 0.3}s;
`;

const StepNumber = styled.div`
  position: absolute;
  top: -10px;
  right: -10px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid currentColor;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 16px;
  color: currentColor;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
`;

const StepContent = styled.div`
  flex: 1;
  position: relative;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const StepTitle = styled.h3`
  font-size: 22px;
  font-weight: 600;
  color: ${props => props.color};
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
  
  @media (max-width: 576px) {
    font-size: 18px;
  }
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.color};
  cursor: pointer;
  font-size: 18px;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;
  margin-left: 10px;
  
  &:hover {
    background: ${props => `${props.color}10`};
  }
  
  svg {
    transition: transform 0.3s ease;
    transform: ${props => props.isExpanded ? 'rotate(180deg)' : 'rotate(0)'};
  }
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const StepDescription = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #666;
  margin-bottom: 10px;
  animation: ${fadeInRight} 0.6s ease ${props => props.delay}s;
  
  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const StepDetails = styled.div`
  font-size: 15px;
  line-height: 1.6;
  color: #777;
  background: rgba(255, 255, 255, 0.7);
  padding: ${props => props.isActive ? '15px' : '0px'};
  border-radius: 8px;
  max-height: ${props => props.isActive ? '200px' : '0'};
  overflow: hidden;
  opacity: ${props => props.isActive ? 1 : 0};
  transform: ${props => props.isActive ? 'translateY(0)' : 'translateY(10px)'};
  transition: all 0.4s ease;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const CtaContainer = styled.div`
  margin-top: 80px;
  text-align: center;
  animation: ${fadeIn} 0.4s ease-out;
  
  @media (max-width: 768px) {
    margin-top: 60px;
  }
`;

const CtaText = styled.p`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const CtaButton = styled.button`
  background: linear-gradient(135deg, #105090, #2090B0);
  background-size: 200% auto;
  color: white;
  border: none;
  border-radius: 30px;
  padding: 14px 34px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  animation: ${gradientAnimation} 3s infinite ease;
  
  &:hover {
    background-position: right center;
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: 768px) {
    padding: 12px 30px;
    font-size: 15px;
  }
`;